import { useState } from 'react';
import type { HostPublicProfile } from '@/mocks/hostProfile';

interface Props {
  profile: HostPublicProfile;
}

const typeLabels: Record<string, string> = {
  concierto: 'Concierto',
  meet_greet: 'Meet & Greet',
  transmision_especial: 'Transmisión especial',
  firma_autografos: 'Firma de autógrafos',
  sesion_acustica: 'Sesión acústica',
  lanzamiento: 'Lanzamiento',
};

const typeIcons: Record<string, string> = {
  concierto: 'ri-mic-2-line',
  meet_greet: 'ri-hand-heart-line',
  transmision_especial: 'ri-broadcast-line',
  firma_autografos: 'ri-pen-nib-line',
  sesion_acustica: 'ri-music-2-line',
  lanzamiento: 'ri-rocket-2-line',
};

export default function HostEventsSection({ profile }: Props) {
  const [tab, setTab] = useState<'publicado' | 'finalizado'>('publicado');

  const allEvents = profile.events;
  const upcoming = allEvents.filter((e) => e.status === 'publicado');
  const finished = allEvents.filter((e) => e.status === 'finalizado' || e.status === 'cancelado');

  const events = tab === 'publicado' ? upcoming : finished;

  if (allEvents.length === 0) return null;

  return (
    <section id="eventos" className="scroll-mt-24">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-calendar-event-line text-accent-500"></i>
          Eventos
        </h2>

        <div className="flex items-center bg-background-100 rounded-full p-1 text-xs">
          <button
            onClick={() => setTab('publicado')}
            className={`px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${tab === 'publicado' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            Próximos ({upcoming.length})
          </button>
          <button
            onClick={() => setTab('finalizado')}
            className={`px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${tab === 'finalizado' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            Finalizados ({finished.length})
          </button>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-foreground-100">
          <div className="w-12 h-12 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-calendar-2-line text-foreground-400 text-lg"></i>
          </div>
          <p className="text-sm text-foreground-500">No hay eventos {tab === 'publicado' ? 'próximos' : 'finalizados'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {events.map((event) => (
            <div key={event.id} className="group bg-white rounded-xl border border-foreground-100 overflow-hidden hover:shadow-sm transition-all cursor-pointer">
              <div className="relative w-full h-40 overflow-hidden">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                {event.highlighted && (
                  <span className="absolute top-2 left-2 bg-accent-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    Destacado
                  </span>
                )}
                <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                  <span className="bg-white/90 text-foreground-800 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                    {typeLabels[event.type] || event.type}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground-950 text-sm group-hover:text-accent-600 transition-colors">{event.title}</h3>
                <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{event.description}</p>
                <div className="flex items-center gap-3 mt-3 text-[10px] text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {event.time}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {event.location.split(' - ')[0]}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-foreground-100">
                  <span className="text-[10px] text-foreground-500 flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {event.participants} participantes
                  </span>
                  <span className="text-[10px] text-foreground-500 flex items-center gap-1">
                    <i className="ri-star-line"></i>
                    {event.interested} interesados
                  </span>
                  {event.status === 'publicado' && (
                    <button className="ml-auto text-[10px] bg-accent-500 text-white font-medium px-3 py-1 rounded-full hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap">
                      Me interesa
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}